<template>
  <div>
  <modal name="redraw-agreement" class="modal-inner modal-redraw-agreement" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" @closed="resetModal">
    <span class="close-button" @click="hide('redraw-agreement')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span v-if="redrawInfo">{{ $t('agreements.menu.redraw_agreement') }} - {{ redrawInfo.agreement_number }}</span>
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formRedrawAgreement" v-if="redrawInfo != null">
        <!-- Original Agreement Info Banner -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div class="flex items-center">
            <BaseIcon icon="info-circle" class="text-blue-500 mr-2" />
            <span class="text-sm text-blue-700">
              {{ $t('agreements.redraw_info_banner') }}: <strong>{{ redrawInfo.agreement_number }}</strong>
            </span>
          </div>
        </div>

        <!-- Customer & Department Selection -->
        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div class="w-full text-sm font-bold italic px-2 mb-3">
            {{ $t('agreement_create.customer') }} / {{ $t('agreement_create.units') }}
          </div>
          <div class="flex flex-wrap">
            <!-- Customer search -->
            <div class="w-full md:w-1/2 px-2 py-2 relative">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('agreement_create.customer') }}</label>
              <input
                type="text"
                v-model="customerSearchQuery"
                @input="onCustomerSearchInput"
                @focus="showCustomerDropdown = true"
                @blur="showCustomerDropdown = false"
                :placeholder="$t('agreements.search_customer_placeholder')"
                class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 px-3 pt-3 pb-2 mt-2"
              />
              <div v-if="customerSearchQuery.length > 0 && customerSearchQuery.length < 3" class="text-xs text-gray-400 mt-1 px-1">
                {{ $t('agreements.min_3_chars') }}
              </div>
              <!-- Dropdown results -->
              <div v-if="showCustomerDropdown && customerSearchResults.length > 0" class="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto" style="left: 0.5rem; right: 0.5rem; width: auto;">
                <div
                  v-for="c in customerSearchResults"
                  :key="c._id"
                  @mousedown.prevent="selectCustomer(c)"
                  class="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50"
                  :class="{ 'bg-blue-100': selectedCustomer && selectedCustomer._id === c._id }"
                >
                  {{ c.name }} <span class="text-xs text-gray-400 ml-1">({{ c.company_number }})</span>
                </div>
              </div>
            </div>
            <!-- Department dropdown -->
            <div class="w-full md:w-1/2 px-2 py-2">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('agreement_create.units') }}</label>
              <select
                v-model="selectedDepartmentId"
                @change="onDepartmentSelected"
                class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 px-3 pt-3 pb-2 mt-2"
                :disabled="!selectedCustomer || departments.length === 0"
              >
                <option value="">{{ $t('agreements.select_department') }}</option>
                <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.agreement_type" :options="agreement_types" nameSelector="name" optionSelector="id" :field_name="$t('agreement_create.agreement_type')" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="newAgreementNumber" type="text" :field_name="$t('agreement_create.agreement_number')" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.e_bill" type="text" :field_name="$t('agreement_create.e_invoice')" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2 mb-4">
            {{ $t("agreement_update_modal.lessee") }}
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.customer_name" type="text" :field_name="$t('agreement_create.name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.company_number" type="text" :field_name="$t('agreement_create.org_number')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.customer_phone" type="text" :field_name="$t('agreement_create.phone')" rules="required" />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.billing_adress" type="text" :field_name="$t('agreement_create.invoice_address')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.billing_zip_code" type="text" :field_name="$t('agreement_create.zip_code')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.billing_city" type="text" :field_name="$t('agreement_create.city')" rules="required" />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.customer_contact" type="text" :field_name="$t('agreement_create.contact_person')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.customer_reference" type="text" :field_name="$t('agreement_create.ref')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2 mb-4">
            {{ $t("agreement_update_modal.lessor") }}
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_name" type="text" :field_name="$t('agreement_update_modal.name')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_company_number" type="text" :field_name="$t('agreement_update_modal.org_number')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_phone" type="text" :field_name="$t('agreement_update_modal.phone')" readonly disabled />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_address" type="text" :field_name="$t('agreement_update_modal.address')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_zip_code" type="text" :field_name="$t('agreement_update_modal.zip_code')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="redrawInfo.dealer_city" type="text" :field_name="$t('agreement_update_modal.ort')" readonly disabled />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.seller" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('agreement_update_modal.seller')" rules="required" />
          </div>
        </div>

        <!-- NOTE: Objektspecifikation section is intentionally removed for redraw -->

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.sim_card" :options="sim_operators" :field_name="$t('agreement_create.sim_card')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.alarm_center" :options="[...alarm_centers, { _id: '-', name: $t('agreement_create.none') }]" nameSelector="name" optionSelector="_id" :field_name="$t('agreement_create.alarm_center')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.agreement_category" :options="agreementCategoryOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.agreement_category')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="insuranceValue" :options="insuranceOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.insurance')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full px-2">
            <div class="flex flex-col">
              <div class="w-full text-gray-600 text-sm font-bold pb-3 font-serif">
                {{ $t('admin_order_alarm.product') }}
              </div>
              
              <!-- Hierarchical Category/Subcategory/Product Display -->
              <div class="space-y-2">
                <div v-for="category in categoriesWithProducts" :key="category.value" class="border border-gray-200 rounded">
                  <!-- Category Header -->
                  <div 
                    class="px-4 py-2 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100"
                    @click="toggleCategory(category.value)"
                  >
                    <div class="flex items-center">
                      <span class="text-sm font-semibold text-gray-800">{{ category.label }}</span>
                      <span class="ml-2 text-xs text-gray-500">({{ groupedProducts[category.value].length }})</span>
                    </div>
                    <span class="text-gray-500 text-xs">{{ expandedCategories[category.value] ? '▲' : '▼' }}</span>
                  </div>
                  
                  <!-- Category Content -->
                  <div v-show="expandedCategories[category.value]" class="bg-white">
                    
                    <!-- Subcategories -->
                    <div v-if="subcategoriesByParent[category.value] && subcategoriesByParent[category.value].length > 0">
                      <div v-for="subcategory in subcategoriesByParent[category.value]" :key="subcategory._id" class="border-b border-gray-100">
                        <!-- Subcategory Header -->
                        <div 
                          class="px-4 py-2 pl-8 bg-blue-50 cursor-pointer flex justify-between items-center hover:bg-blue-100"
                          @click="toggleSubcategory(subcategory._id)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-blue-800">{{ subcategory.displayName }}</span>
                            <span class="ml-2 text-xs text-blue-600">({{ getProductsForSubcategory(category.value, subcategory._id).length }})</span>
                          </div>
                          <span class="text-blue-500 text-xs">{{ expandedSubcategories[subcategory._id] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <!-- Subcategory Products -->
                        <div v-show="expandedSubcategories[subcategory._id]" class="pl-8 pr-2 py-2 space-y-2">
                          <div v-for="p in getProductsForSubcategory(category.value, subcategory._id)" :key="p._id || p.id || p.type">
                            <!-- Product Row -->
                            <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <!-- Variant Rows -->
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Products Without Subcategory -->
                      <div v-if="getProductsWithoutSubcategory(category.value).length > 0" class="border-b border-gray-100">
                        <div 
                          class="px-4 py-2 pl-8 bg-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-200"
                          @click="toggleSubcategory(`${category.value}-none`)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-gray-600">{{ $t('products.without_subcategory') }}</span>
                            <span class="ml-2 text-xs text-gray-500">({{ getProductsWithoutSubcategory(category.value).length }})</span>
                          </div>
                          <span class="text-gray-500 text-xs">{{ expandedSubcategories[`${category.value}-none`] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <div v-show="expandedSubcategories[`${category.value}-none`]" class="pl-8 pr-2 py-2 space-y-2">
                          <div v-for="p in getProductsWithoutSubcategory(category.value)" :key="p._id || p.id || p.type">
                            <!-- Product Row -->
                            <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <!-- Variant Rows -->
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- No Subcategories: Show products directly -->
                    <div v-else class="px-4 py-2 space-y-2">
                      <div v-for="p in groupedProducts[category.value]" :key="p._id || p.id || p.type">
                        <!-- Product Row -->
                        <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                          <div class="w-2/5 text-sm flex items-center">
                            <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                              {{ expandedVariants[p._id] ? '▼' : '▶' }}
                            </button>
                            <div class="flex-1">
                              <template v-if="p.display_name">
                                <div class="font-semibold">{{ p.display_name }}</div>
                                <div class="text-xs text-gray-500">{{ p.name }}</div>
                              </template>
                              <template v-else>
                                <span class="font-medium">{{ p.name }}</span>
                              </template>
                              <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                            </div>
                          </div>
                          <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                            <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                          </template>
                          <template v-else>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                          </template>
                        </div>
                        <!-- Variant Rows -->
                        <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                          <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                            <div class="w-2/5 text-sm pl-2 flex items-center">
                              <span class="text-blue-500 mr-2">↳</span>
                              <div>
                                <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                              </div>
                            </div>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2" v-if="redrawInfo.agreement_category === 'cash_services'">
            <BaseInput v-model.number="totalCashPrice" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.total_cash_price')" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="redrawInfo.agreement_category === 'rental' || redrawInfo.agreement_category === 'financed' || redrawInfo.agreement_category === 'financed_umea' || redrawInfo.agreement_category === 'financed_ikano'">
            <BaseInput v-model.number="cashSumForRentalFinanced" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.cash_sum')" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.contract_period" :options="contractPeriodOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.contract_period')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isContractFreetext">
            <BaseInput v-model="redrawInfo.contract_period_freetext" type="text" :field_name="$t('agreement_create.contract_period_freetext_label')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model.number="redrawInfo.setup_cost" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.setup_fee')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="showRentalPrice">
            <BaseInput v-model.number="redrawInfo.rental_month_cost" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.rental_cost')" rules="required" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="redrawInfo.payment_terms" :options="paymentTermsOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.payment_terms')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isPaymentTermsFreetext">
            <BaseInput v-model="redrawInfo.payment_terms_freetext" type="text" :field_name="$t('agreement_create.payment_terms_freetext_label')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <ValidationProvider :name="$t('agreement_update_modal.contract_start')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("admin_order_alarm.contract_start") }}<b class="text-red-500 ml-1">*</b></label>
                <date-picker :lang="datePickerLocale" :key="date_picker_langKey" class="mt-2" v-model="redrawInfo.contract_start" value-type="format" format="YYYY-MM-DD" :editable="false" :clearable="false"></date-picker>
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <div class="input-validate">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("admin_order_alarm.delivery_deadline") }}</label>
              <date-picker :lang="datePickerLocale" :key="'dp-delivery-' + date_picker_langKey" class="mt-2" v-model="delivery_period" value-type="format" format="YYYY-MM-DD" :editable="false" :clearable="true"></date-picker>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full px-2 py-2">
            <BaseTextarea rows="4" v-model="redrawInfo.special_terms" :field_name="$t('admin_order_alarm.special_terms')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 px-2 py-2">
            <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("agreement_update_modal.signed_agreement") }}</label>
            <div class="relative">
              <input type="file" accept="application/pdf" ref="pdfCreate" @change="fileChangeCreate()" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
              <div class="bg-gray-100 rounded w-full text-sm text-gray-900 border-b-4 border-gray-300 px-3 pt-2 pb-2 flex items-center">
                <span class="btn-blue-outline px-3 py-1 mr-3">{{ $t("file_input.choose_file") }}</span>
                <span class="text-gray-500">{{ selectedPdfName || $t("file_input.no_file_selected") }}</span>
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide('redraw-agreement')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-green px-3 py-2 float-right" @click="confirmRedraw()">{{ $t('agreements.menu.redraw_agreement') }}<BaseIcon icon="sync-alt" class="ml-1" /></button>
      </div>
    </div>
  </modal>

  <modal name="redraw-order-selection" class="modal-inner" transition="pop-out" :width="orderSelectionModalWidth" :focus-trap="true" height="auto" :scrollable="true" @closed="resetOrderProductSelectionState">
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span>{{ $t('agreements.confirm_generate_order') }}</span>
    </div>
    <div class="modal-body">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div class="flex items-start gap-2">
          <BaseIcon icon="clipboard-list" class="text-blue-500 mt-0.5" />
          <div>
            <div class="text-sm font-semibold text-blue-800">{{ newAgreementNumber }}</div>
            <div class="text-sm text-blue-700">{{ $t('agreements.redraw_order_selection_info') }}</div>
          </div>
        </div>
      </div>
      <div v-if="orderSelectionItems.length > 0">
        <div class="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
          <input
            id="redraw-order-select-all"
            type="checkbox"
            :checked="allOrderProductsSelected"
            @change="toggleAllOrderProducts"
            class="w-4 h-4 text-blue-600 rounded"
          />
          <label for="redraw-order-select-all" class="text-sm font-medium text-gray-700">
            {{ $t('agreements.select_all') }} ({{ orderSelectionItems.length }})
          </label>
        </div>

        <div class="max-h-96 overflow-y-auto space-y-2 pr-1">
          <label
            v-for="item in orderSelectionItems"
            :key="item.key"
            :for="item.key"
            class="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer"
          >
            <input
              :id="item.key"
              v-model="selectedOrderProductKeys"
              type="checkbox"
              :value="item.key"
              class="w-4 h-4 text-blue-600 rounded mt-0.5"
            />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-800">{{ item.label }}</div>
              <div v-if="item.subLabel" class="text-xs text-gray-500">{{ item.subLabel }}</div>
            </div>
            <div class="text-right text-xs text-gray-500 whitespace-nowrap">
              <div>{{ $t('agreement_create.quantity') }}: {{ item.qty }}</div>
              <div v-if="item.price > 0">{{ formatAmount(item.price) }}</div>
            </div>
          </label>
        </div>
      </div>

      <div v-else class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {{ $t('agreements.redraw_order_selection_empty') }}
      </div>

      <!-- Everything else a new order asks for, the same fields as the order form.
           Started from the original agreement's order; checked on "Generate order". -->
      <div class="mt-6 pt-4 border-t border-secondary-400">
        <div class="text-gray-700 text-md font-bold font-serif px-2">{{ $t('agreements.redraw_order_details') }}</div>
        <p class="text-xs italic text-gray-500 px-2 pb-2">{{ originalOrder ? $t('agreements.redraw_order_details_prefilled', { number: agreementInfo && agreementInfo.agreement_number }) : $t('agreements.redraw_order_details_empty') }}</p>
        <OrderDetailsFields
          v-if="redrawInfo"
          ref="orderDetails"
          :initialOrder="originalOrder"
          :defaultCustomerInfo="orderDefaultCustomerInfo"
          :alarmCenterId="redrawInfo.alarm_center || ''"
          :alarmCenterName="selectedAlarmCenterName"
          :country="agreementCountry()"
          :alarmDeviceCount="selectedAlarmDeviceCount"
        />
        <!-- the order's delivery deadline is required, as on the order form -->
        <div class="mt-4 px-2 md:w-1/2">
          <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("admin_order_alarm.delivery_deadline") }}<b class="text-red-500 ml-1">*</b></label>
          <date-picker :lang="datePickerLocale" :key="'dp-order-delivery-' + date_picker_langKey" class="mt-2" v-model="delivery_period" value-type="format" format="YYYY-MM-DD" :editable="false" :clearable="true"></date-picker>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="closeOrderSelectionModal()">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-secondary-outline px-3 py-2 float-left ml-2" @click="skipOrderGeneration()">{{ $t('user_actions.no') }}</button>
        <button class="btn-green px-3 py-2 float-right disabled:opacity-60 disabled:cursor-not-allowed" :disabled="!canGenerateOrderFromSelection" @click="confirmOrderGeneration()">{{ $t('agreements.redraw_generate_order_button') }}<BaseIcon icon="shopping-cart" class="ml-1" /></button>
      </div>
    </div>
  </modal>
  </div>
</template>

<script>
const MODAL_WIDTH = 900;

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import siteContext from "@/utils/siteContext";
import agreementCostMixin from "@/mixins/agreementCostMixin";
import OrderDetailsFields from "@/components/order/OrderDetailsFields.vue";


export default {
  siteContext,
  mixins: [agreementCostMixin],
  props: {
    agreementInfo: {
      required: true,
      default: null,
    },
  },
  components: {
    DatePicker,
    OrderDetailsFields,
  },
  data() {
    return {
      user: this.$store.state.user,
      // the original agreement's order: the redraw order's details start from it
      originalOrder: null,
      // the order details, taken from the step-2 form before it closes
      pendingOrderDetails: null,
      sellers: [],
      alarm_centers: [],
      redrawInfo: null,
      newAgreementNumber: '',
      selected_pdf_file: null,
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      defaultProducts: [],
      productsFromDB: [],
      subcategories: [],
      // country the sellers/alarm centers/subcategories were loaded for
      listsCountry: null,
      expandedCategories: {},
      expandedSubcategories: {},
      expandedVariants: {},
      // Customer & department selection
      customerSearchQuery: '',
      customerSearchResults: [],
      showCustomerDropdown: false,
      selectedCustomer: null,
      departments: [],
      selectedDepartmentId: '',
      customerSearchTimer: null,
      selectedOrderProductKeys: [],
      delivery_period: null,
    };
  },
  computed: {
    // as wide as the redraw form: step 2 holds the whole order form's details
    orderSelectionModalWidth() {
      return this.modalWidth || MODAL_WIDTH;
    },
    selectedPdfName() {
      return this.selected_pdf_file ? this.selected_pdf_file.name : null;
    },
    sim_operators() {
      return this.productsFromDB
        .filter(p => p.category === 'simcard_supplier')
        .map(p => p.name);
    },
    categoryOptions() {
      return [
        { value: 'alarm', label: this.$t('sales_dashboard.cat_personal_alarm') },
        { value: 'tag', label: this.$t('sales_dashboard.cat_tags') },
        { value: 'display', label: this.$t('sales_dashboard.cat_display') },
        { value: 'ajax', label: this.$t('sales_dashboard.cat_perimeter') },
        { value: 'vehicle_tracker', label: this.$t('sales_dashboard.cat_vehicle_tracker') },
        { value: 'software', label: this.$t('sales_dashboard.cat_software') },
        { value: 'simcard', label: this.$t('sales_dashboard.cat_simcard') },
        { value: 'accessory', label: this.$t('sales_dashboard.cat_accessory') },
        { value: 'other', label: this.$t('sales_dashboard.cat_other') }
      ];
    },
    agreement_types() {
      return [
        { id: "customer", name: this.$t("agreement_create.customers") },
        { id: "reseller", name: this.$t("agreement_create.resellers") },
      ];
    },
    agreementCategoryOptions() {
      return [
        { value: 'rental', name: this.$t('admin_order_alarm.category_rental') },
        { value: 'cash_services', name: this.$t('admin_order_alarm.category_cash_services') },
        { value: 'financed', name: this.$t('admin_order_alarm.category_financed') }
      ];
    },
    insuranceOptions() {
      return [
        { value: 'yes', name: this.$t('yes') },
        { value: 'no', name: this.$t('no') }
      ];
    },
    insuranceValue: {
      get() {
        if (!this.redrawInfo) return 'yes';
        return this.redrawInfo.insurance === false ? 'no' : 'yes';
      },
      set(val) {
        if (this.redrawInfo) {
          this.redrawInfo.insurance = val === 'yes';
        }
      }
    },
    showRentalPrice() {
      return this.redrawInfo && ['rental', 'financed', 'financed_umea', 'financed_ikano', 'cash_services'].includes(this.redrawInfo.agreement_category);
    },
    showCashPrice() {
      return this.redrawInfo && this.redrawInfo.agreement_category === 'cash_services';
    },
    visibleProducts() {
      if (!this.redrawInfo || !this.redrawInfo.products) return [];
      return this.redrawInfo.products.filter(p => {
        if (p.always_visible) return true;
        return true;
      });
    },
    hardwareProducts() {
      return this.visibleProducts.filter(p => !p.is_software);
    },
    softwareProducts() {
      return this.visibleProducts.filter(p => p.is_software);
    },
    totalCashPrice() {
      if (!this.redrawInfo || !this.redrawInfo.products) return 0;
      return this.redrawInfo.products.reduce((total, p) => {
        // Skip software/monthly products
        if (p.is_software || p.category === 'simcard' || p.category === 'software' || 
            (p.category === 'accessory' && p.type && p.type.includes('monthly')) ||
            (p.category === 'other' && p.type && p.type.includes('monthly'))) {
          return total;
        }
        
        // Add main product cash price
        if (p.qty > 0 && p.cash_price > 0) {
          total += (p.qty * p.cash_price);
        }
        
        // Add variant cash prices
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            if ((v.qty || 0) > 0 && (v.cash_price || 0) > 0) {
              total += (v.qty * v.cash_price);
            }
          });
        }
        
        return total;
      }, 0);
    },
    cashSumForRentalFinanced() {
      if (!this.redrawInfo || !this.redrawInfo.products) return 0;
      if (!['rental', 'financed', 'financed_umea', 'financed_ikano'].includes(this.redrawInfo.agreement_category)) return 0;
      return this.redrawInfo.products.reduce((total, p) => {
        // Only include addon_cash and other_cash type products
        if (((p.category === 'accessory' || p.category === 'other') && p.type && p.type.includes('cash'))) {
          // Add main product cash price
          if (p.qty > 0 && p.cash_price > 0) {
            total += (p.qty * p.cash_price);
          }
          
          // Add variant cash prices
          if (p.variants && p.variants.length > 0) {
            p.variants.forEach(v => {
              if ((v.qty || 0) > 0 && (v.cash_price || 0) > 0) {
                total += (v.qty * v.cash_price);
              }
            });
          }
        }
        return total;
      }, 0);
    },
    paymentTermsOptions() {
      const isNorwegian = this.agreementCountry() === 'no';
      
      if (isNorwegian) {
        return [
          { value: "Årlig", name: this.$t("agreement_create.payment_terms_yearly") },
          { value: "Halvårlig", name: this.$t("agreement_create.payment_terms_half_year") },
          { value: "Kvartalsvis", name: this.$t("agreement_create.payment_terms_quarterly") },
          { value: "Månedlig", name: this.$t("agreement_create.payment_terms_monthly") },
          { value: "__fritekst__", name: this.$t("agreement_create.payment_terms_freetext") },
        ];
      }
      
      return [
        { value: "Årsvis", name: this.$t("agreement_create.payment_terms_yearly") },
        { value: "Halv år", name: this.$t("agreement_create.payment_terms_half_year") },
        { value: "Kvartal", name: this.$t("agreement_create.payment_terms_quarterly") },
        { value: "Månad", name: this.$t("agreement_create.payment_terms_monthly") },
        { value: "__fritext__", name: this.$t("agreement_create.payment_terms_freetext") },
      ];
    },
    contractPeriodOptions() {
      const months = this.$t('admin_order_alarm.months');
      return [
        { value: 0, name: `0 ${months}` },
        { value: 12, name: `12 ${months}` },
        { value: 24, name: `24 ${months}` },
        { value: 36, name: `36 ${months}` },
        { value: 48, name: `48 ${months}` },
        { value: 60, name: `60 ${months}` },
        { value: 72, name: `72 ${months}` },
        { value: 84, name: `84 ${months}` },
        { value: 96, name: `96 ${months}` },
        { value: 108, name: `108 ${months}` },
        { value: 120, name: `120 ${months}` },
        { value: 'freetext', name: this.$t('admin_order_alarm.contract_period_freetext') }
      ];
    },
    isContractFreetext() {
      return this.redrawInfo && this.redrawInfo.contract_period === 'freetext';
    },
    isPaymentTermsFreetext() {
      return this.redrawInfo && (this.redrawInfo.payment_terms === '__fritekst__' || this.redrawInfo.payment_terms === '__fritext__');
    },
    datePickerLocale() {
      return this.date_picker_lang === 'no' ? 'nb' : this.date_picker_lang;
    },
    // Group products by category
    groupedProducts() {
      if (!this.redrawInfo || !this.redrawInfo.products) return {};
      const grouped = {};
      this.redrawInfo.products.forEach(product => {
        const key = product.category || 'other';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(product);
      });
      return grouped;
    },
    // Get subcategories for a specific category
    subcategoriesByParent() {
      const grouped = {};
      this.subcategories.forEach(sub => {
        if (!grouped[sub.parentCategory]) {
          grouped[sub.parentCategory] = [];
        }
        grouped[sub.parentCategory].push(sub);
      });
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });
      return grouped;
    },
    // Categories that have products
    categoriesWithProducts() {
      return this.categoryOptions.filter(cat => 
        this.groupedProducts[cat.value] && this.groupedProducts[cat.value].length > 0
      );
    },
    // Categories that have products with qty > 0 (from old agreement)
    categoriesWithActiveProducts() {
      const activeCategories = new Set();
      if (!this.redrawInfo || !this.redrawInfo.products) return activeCategories;
      
      this.redrawInfo.products.forEach(p => {
        if (p.qty > 0 || (p.variants && p.variants.some(v => v.qty > 0))) {
          if (p.category) {
            activeCategories.add(p.category);
          }
        }
      });
      return activeCategories;
    },
    // Orderer details for the order when the original order has none: the agreement's customer
    orderDefaultCustomerInfo() {
      const info = this.redrawInfo || {};
      return {
        contact_name: info.customer_contact || "",
        contact_phone: info.customer_phone || "",
        company_name: info.customer_name || "",
        invoice_detail: [info.billing_adress, [info.billing_zip_code, info.billing_city].filter(Boolean).join(" ")].filter(Boolean).join("\n"),
      };
    },
    selectedAlarmCenterName() {
      const center = (this.alarm_centers || []).find((c) => this.redrawInfo && c._id === this.redrawInfo.alarm_center);
      return (center && center.name) || "";
    },
    // alarm devices among the products picked for the order (one personal block each)
    selectedAlarmDeviceCount() {
      return this.buildSelectedOrderProducts()
        .filter((product) => product.category === "alarm")
        .reduce((count, product) => count + (product.qty || 0) + (product.variants || []).reduce((sum, v) => sum + (v.qty || 0), 0), 0);
    },
    orderSelectionItems() {
      if (!this.redrawInfo || !Array.isArray(this.redrawInfo.products)) return [];

      const items = [];

      this.redrawInfo.products.forEach(product => {
        if ((product.qty || 0) > 0) {
          items.push({
            key: this.getOrderItemKey(product),
            label: product.display_name || product.name,
            subLabel: product.display_name ? product.name : product.articleNumber || '',
            qty: product.qty || 0,
            price: this.getOrderItemPrice(product),
          });
        }

        (product.variants || []).forEach(variant => {
          if ((variant.qty || 0) > 0) {
            items.push({
              key: this.getOrderItemKey(product, variant),
              label: `${product.display_name || product.name} - ${variant.name}`,
              subLabel: variant.articleNumber || product.articleNumber || '',
              qty: variant.qty || 0,
              price: this.getOrderItemPrice(variant),
            });
          }
        });
      });

      return items;
    },
    allOrderProductsSelected() {
      return this.orderSelectionItems.length > 0 && this.selectedOrderProductKeys.length === this.orderSelectionItems.length;
    },
    canGenerateOrderFromSelection() {
      return this.selectedOrderProductKeys.length > 0 && !!this.delivery_period;
    },
  },
  watch: {
    user() {
      return this.$store.state.user;
    },
    agreementInfo: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;

        this.redrawInfo = _.cloneDeep(newVal);

        // The lists follow the agreement's country - reload them when it differs
        // from the ones loaded (e.g. a Norwegian agreement from the Swedish portal)
        if (this.listsCountry && this.listsCountry !== this.agreementCountry()) {
          this.loadCountryLists();
        }

        // Generate new agreement number for the redrawn agreement
        this.generateNewAgreementNumber(newVal.agreement_number);

        // Reset contract start to today for the new agreement
        this.redrawInfo.contract_start = this.moment().format('YYYY-MM-DD');

        // Store original agreement number for reference
        this.redrawInfo.original_agreement_number = newVal.agreement_number;

        // Append redraw notice to special_terms
        const redrawNotice = `\n${this.$t('agreements.redraw_notice_text', { agreement: newVal.agreement_number })}`;
        if (this.redrawInfo.special_terms) {
          this.redrawInfo.special_terms = this.redrawInfo.special_terms + redrawNotice;
        } else {
          this.redrawInfo.special_terms = redrawNotice.trim();
        }

        // Initialize freetext fields if not present
        if (!this.redrawInfo.contract_period_freetext) {
          this.redrawInfo.contract_period_freetext = '';
        }
        if (!this.redrawInfo.payment_terms_freetext) {
          this.redrawInfo.payment_terms_freetext = '';
        }

        // Detect if payment_terms is freetext (doesn't match standard options)
        this.initializeFreetextPaymentTerms();

        // Fetch all products from database and merge with existing agreement products
        this.fetchAndMergeProducts(newVal.products || []);

        // Set default agreement_category if not set
        if (!this.redrawInfo.agreement_category) {
          this.redrawInfo.agreement_category = 'rental';
        }

        this.selected_pdf_file = null;

        // Pre-fetch customer & department from the original agreement's company_number
        this.prefetchCustomerAndDepartment(newVal);
      },
    },
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
    'redrawInfo.agreement_category'() {
      // Regenerate agreement number when category changes to/from financed
      if (this.redrawInfo && this.redrawInfo.original_agreement_number) {
        this.generateNewAgreementNumber(this.redrawInfo.original_agreement_number);
      }
    },
  },
  methods: {
    /**
     * Pre-fetch customer and department using the original agreement's company_number.
     * Looks up the unit by company_number, then fetches the parent customer and
     * sibling departments. Pre-selects the matching customer and department.
     */
    async prefetchCustomerAndDepartment(agreement) {
      if (!agreement || !agreement.company_number) return;

      try {
        const params = new URLSearchParams({ company_number: agreement.company_number, country: this.agreementCountry() });
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/units/lookup?${params.toString()}`
        );

        const unit = response.data.data;
        const customer = response.data.customer;
        if (!unit || !customer) return;

        // Set customer in search field
        this.selectedCustomer = customer;
        this.customerSearchQuery = customer.name;

        // Fetch all departments for this customer
        const deptResponse = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/units/customer/${customer._id}`,
          { params: { country: this.agreementCountry() } }
        );
        this.departments = deptResponse.data.units || [];

        // Pre-select the matching department
        this.selectedDepartmentId = unit._id;

        // Apply the department data to the form fields
        this.applyDepartmentData(unit, customer);
      } catch (error) {
        console.warn('Could not pre-fetch customer/department for redraw.', error);
      }
    },

    /**
     * Handles customer search input with debounce (300ms).
     * Requires at least 3 characters before searching.
     */
    onCustomerSearchInput() {
      // Reset selection when user types
      this.selectedCustomer = null;
      this.departments = [];
      this.selectedDepartmentId = '';

      if (this.customerSearchTimer) clearTimeout(this.customerSearchTimer);

      if (this.customerSearchQuery.length < 3) {
        this.customerSearchResults = [];
        return;
      }

      this.customerSearchTimer = setTimeout(() => {
        this.searchCustomers();
      }, 300);
    },

    async searchCustomers() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/customer/search`,
          { params: { q: this.customerSearchQuery, country: this.agreementCountry() } }
        );
        this.customerSearchResults = response.data.data || [];
        this.showCustomerDropdown = true;
      } catch (error) {
        console.warn('Customer search failed:', error);
        this.customerSearchResults = [];
      }
    },

    /**
     * Called when a customer is selected from the search dropdown.
     * Fetches the departments (units) for that customer.
     */
    async selectCustomer(customer) {
      this.selectedCustomer = customer;
      this.customerSearchQuery = customer.name;
      this.showCustomerDropdown = false;
      this.customerSearchResults = [];
      this.selectedDepartmentId = '';

      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/units/customer/${customer._id}`,
          { params: { country: this.agreementCountry() } }
        );
        this.departments = response.data.units || [];

        // If only one department, auto-select it
        if (this.departments.length === 1) {
          this.selectedDepartmentId = this.departments[0]._id;
          this.onDepartmentSelected();
        }
      } catch (error) {
        console.warn('Failed to fetch departments:', error);
        this.departments = [];
      }
    },

    /**
     * Called when a department is selected from the dropdown.
     * Applies the department's data to the redraw form fields.
     */
    onDepartmentSelected() {
      if (!this.selectedDepartmentId) return;

      const dept = this.departments.find(d => d._id === this.selectedDepartmentId);
      if (!dept) return;

      this.applyDepartmentData(dept, this.selectedCustomer);
    },

    /**
     * Applies department (unit) data to all lessee fields in the redraw form.
     */
    applyDepartmentData(unit, customer) {
      if (!this.redrawInfo || !unit) return;

      const customerName = customer && customer.name
        ? `${customer.name} - ${unit.name}`
        : unit.name || this.redrawInfo.customer_name;

      this.$set(this.redrawInfo, 'customer_name', customerName);
      this.$set(this.redrawInfo, 'company_number', unit.company_number || '');
      this.$set(this.redrawInfo, 'customer_phone', unit.contact_phone || '');
      this.$set(this.redrawInfo, 'billing_adress', unit.billing_address || '');
      this.$set(this.redrawInfo, 'billing_zip_code', unit.billing_zip_code || '');
      this.$set(this.redrawInfo, 'billing_city', unit.billing_city || '');
      this.$set(this.redrawInfo, 'customer_contact', unit.contact_name || '');
      this.$set(this.redrawInfo, 'customer_reference', unit.billing_reference || '');
      this.$set(this.redrawInfo, 'e_bill', unit.billing_invoice || '');
    },

    initializeFreetextPaymentTerms() {
      if (!this.redrawInfo || !this.redrawInfo.payment_terms) return;
      
      // Standard payment terms values (both Norwegian and Swedish)
      const standardOptions = [
        'Årlig', 'Halvårlig', 'Kvartalsvis', 'Månedlig', '__fritekst__',  // Norwegian
        'Årsvis', 'Halv år', 'Kvartal', 'Månad', '__fritext__'  // Swedish
      ];
      
      const isFreetextMarker = this.redrawInfo.payment_terms === '__fritekst__' || this.redrawInfo.payment_terms === '__fritext__';
      
      // Case 1: payment_terms is already a freetext marker - ensure freetext field is initialized with $set for reactivity
      if (isFreetextMarker) {
        if (!this.redrawInfo.payment_terms_freetext) {
          this.$set(this.redrawInfo, 'payment_terms_freetext', '');
        }
        return;
      }
      
      // Case 2: payment_terms is not a standard option - it's a freetext value saved directly
      if (!standardOptions.includes(this.redrawInfo.payment_terms)) {
        const isNorwegian = this.agreementCountry() === 'no';
        // Store the original payment_terms value as freetext (use $set for reactivity)
        this.$set(this.redrawInfo, 'payment_terms_freetext', this.redrawInfo.payment_terms);
        // Set the dropdown to the freetext marker
        this.redrawInfo.payment_terms = isNorwegian ? '__fritekst__' : '__fritext__';
      }
    },
    generateNewAgreementNumber(originalNumber) {
      // Strip any financed suffix (PIF/FINU/FINI with or without leading dash) to get a clean base number.
      // Handles both SE2026-1234PIF and SE2026-1234-OMT-PIF forms.
      let cleanNumber = originalNumber.replace(/-?(PIF|FINU|FINI)$/, '');
      
      // Check if the agreement is already a redrawn agreement
      // Pattern: ends with -OMT or -OMT{number}
      const omtPattern = /-OMT(\d*)$/;
      const match = cleanNumber.match(omtPattern);
      
      let newNumber;
      if (match) {
        // This is already a redrawn agreement
        const currentNumber = match[1] ? parseInt(match[1]) : 1; // If no number, it's OMT1
        const baseNumber = cleanNumber.replace(omtPattern, ''); // Remove the -OMT suffix
        
        // Increment the number
        const nextNumber = currentNumber + 1;
        newNumber = `${baseNumber}-OMT${nextNumber}`;
      } else {
        // First time redrawing this agreement
        newNumber = `${cleanNumber || ''}-OMT`;
      }
      
      // Always add financed suffix when the new agreement category is financed
      const financedSuffixMap = { 'financed': 'PIF', 'financed_umea': 'FINU', 'financed_ikano': 'FINI' };
      const suffix = this.redrawInfo && financedSuffixMap[this.redrawInfo.agreement_category];
      if (suffix) {
        newNumber = `${newNumber}-${suffix}`;
      }
      
      this.newAgreementNumber = newNumber;
    },
    
    // Everything country-bound in the form (product catalog, sellers, alarm
    // centers, customers, payment terms) follows the agreement's country, not
    // the portal's - a Norwegian agreement redrawn from the Swedish portal
    // stays Norwegian (the server keeps the original agreement's country).
    agreementCountry() {
      return (this.agreementInfo && this.agreementInfo.country) || this.$options.siteContext.getSiteContext();
    },
    loadCountryLists() {
      this.listsCountry = this.agreementCountry();
      this.getSellers();
      this.getAlarmCenters();
      this.fetchSubcategories();
    },
    getSellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/sellers/`, { params: { country: this.agreementCountry() } })
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },
    async fetchSubcategories() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/subcategories`, { params: { country: this.agreementCountry() } });
        this.subcategories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      }
    },
    // Get products that belong to a specific subcategory within a category
    getProductsForSubcategory(categoryValue, subcategoryId) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => {
        if (!p.subcategory) return false;
        const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
        return String(productSubcatId) === String(subcategoryId);
      });
    },
    // Get products without any subcategory within a category
    getProductsWithoutSubcategory(categoryValue) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => !p.subcategory);
    },
    // Toggle category expansion
    toggleCategory(categoryValue) {
      this.$set(this.expandedCategories, categoryValue, !this.expandedCategories[categoryValue]);
    },
    // Toggle subcategory expansion
    toggleSubcategory(subcategoryId) {
      this.$set(this.expandedSubcategories, subcategoryId, !this.expandedSubcategories[subcategoryId]);
    },
    // Toggle product variants expansion
    toggleVariants(productId) {
      this.$set(this.expandedVariants, productId, !this.expandedVariants[productId]);
    },
    // Get category label from value
    getCategoryLabel(categoryValue) {
      const cat = this.categoryOptions.find(c => c.value === categoryValue);
      return cat ? cat.label : categoryValue;
    },
    
    getContractPeriodValue() {
      // If freetext is selected, return the custom text instead
      if (this.isContractFreetext) {
        return this.redrawInfo.contract_period_freetext;
      }
      return this.redrawInfo.contract_period;
    },
    
    getPaymentTermsValue() {
      // If freetext is selected, return the custom text instead
      if (this.isPaymentTermsFreetext) {
        return this.redrawInfo.payment_terms_freetext;
      }
      return this.redrawInfo.payment_terms;
    },

    getOrderItemKey(product, variant = null) {
      const productId = product && (product._id || product.id || product.name || product.type || 'product');
      if (variant) {
        const variantId = variant._id || variant.id || variant.name || 'variant';
        return `variant:${productId}:${variantId}`;
      }
      return `product:${productId}`;
    },

    getOrderItemPrice(item) {
      if (!this.redrawInfo) return 0;
      return this.redrawInfo.agreement_category === 'cash_services'
        ? (item.cash_price || item.price || 0)
        : (item.rental_price || item.price || 0);
    },

    resetOrderProductSelectionState() {
      this.selectedOrderProductKeys = [];
    },

    async openOrderSelectionModal() {
      this.selectedOrderProductKeys = this.orderSelectionItems.map(item => item.key);
      this.pendingOrderDetails = null;
      await this.fetchOriginalOrder();
      this.$modal.show('redraw-order-selection');
    },

    // The original agreement's order, so the new order's details start from it.
    // None (older agreements) -> the details start empty.
    async fetchOriginalOrder() {
      this.originalOrder = null;
      const number = this.agreementInfo && this.agreementInfo.agreement_number;
      if (!number) return;
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/order-alarm/by-agreement/${encodeURIComponent(number)}`);
        this.originalOrder = response.data.order || null;
      } catch (error) {
        console.error('Failed to fetch the original order:', error);
      }
    },

    closeOrderSelectionModal() {
      this.$modal.hide('redraw-order-selection');
    },

    toggleAllOrderProducts() {
      if (this.allOrderProductsSelected) {
        this.selectedOrderProductKeys = [];
        return;
      }

      this.selectedOrderProductKeys = this.orderSelectionItems.map(item => item.key);
    },

    buildSelectedOrderProducts() {
      if (!this.redrawInfo || !Array.isArray(this.redrawInfo.products)) return [];

      return this.redrawInfo.products
        .map(product => {
          const includeProduct = this.selectedOrderProductKeys.includes(this.getOrderItemKey(product));
          const selectedVariants = (product.variants || []).filter(variant =>
            this.selectedOrderProductKeys.includes(this.getOrderItemKey(product, variant))
          );

          if (!includeProduct && selectedVariants.length === 0) {
            return null;
          }

          return {
            ...product,
            qty: includeProduct ? product.qty : 0,
            variants: selectedVariants,
          };
        })
        .filter(Boolean);
    },

    skipOrderGeneration() {
      this.closeOrderSelectionModal();
      this.executeRedraw(false);
    },

    // The order details are checked like on the order form, and taken before the
    // step-2 modal closes (closing it removes the form)
    async confirmOrderGeneration() {
      if (!this.canGenerateOrderFromSelection) {
        return;
      }
      const orderDetails = this.$refs.orderDetails;
      if (orderDetails && !(await orderDetails.validate())) return;
      this.pendingOrderDetails = orderDetails ? orderDetails.buildPayload() : null;

      this.closeOrderSelectionModal();
      this.executeRedraw(true);
    },
    
    calculateTotalPrice(){
      let total = 0;
      const category = this.redrawInfo.agreement_category;
      
      this.redrawInfo.products.forEach(p => {
        // Calculate for main product
        if (category === 'cash_services') {
          if (p.is_software || p.category === 'simcard' || p.category === 'software' ||
              (p.category === 'accessory' && p.type && p.type.includes('monthly')) ||
              (p.category === 'other' && p.type && p.type.includes('monthly'))) {
            total += p.qty * (p.rental_price || p.price || 0);
          }
        } else {
          if (!((p.category === 'accessory' || p.category === 'other') && p.type && p.type.includes('cash'))) {
            total += p.qty * (p.rental_price || p.price || 0);
          }
        }
        
        // Calculate for variants
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            if (category === 'cash_services') {
              if (p.is_software || p.category === 'simcard' || p.category === 'software' ||
                  (p.category === 'accessory' && p.type && p.type.includes('monthly')) ||
                  (p.category === 'other' && p.type && p.type.includes('monthly'))) {
                total += (v.qty || 0) * (v.rental_price || v.price || 0);
              }
            } else {
              if (!((p.category === 'accessory' || p.category === 'other') && p.type && p.type.includes('cash'))) {
                total += (v.qty || 0) * (v.rental_price || v.price || 0);
              }
            }
          });
        }
      });
      this.redrawInfo.rental_month_cost = total;
    },
    
    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault();
      }
    },
    
    getAlarmCenters() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params: { country: this.agreementCountry() } })
        .then((response) => {
          this.alarm_centers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    confirmRedraw() {
      this.$refs.formRedrawAgreement.validate().then((success) => {
        if (!success) {
          return;
        }

        this.openOrderSelectionModal();
      });
    },

    executeRedraw(generateOrder) {
      let headers = {
        "Content-Type": "multipart/form-data",
        "X-Access-Token": this.$store.state.token,
      };

      let formData = new FormData();

      // Calculate cash_sum based on agreement_category
      let cash_sum = '';
      if (this.redrawInfo.agreement_category === 'rental' || this.redrawInfo.agreement_category === 'financed' || this.redrawInfo.agreement_category === 'financed_umea' || this.redrawInfo.agreement_category === 'financed_ikano') {
        cash_sum = this.cashSumForRentalFinanced.toString();
      } else if (this.redrawInfo.agreement_category === 'cash_services') {
        cash_sum = this.totalCashPrice.toString();
      }

      // Original agreement ID to be set to active_old
      formData.append("original_agreement_id", this.agreementInfo._id);
      formData.append("original_agreement_number", this.agreementInfo.agreement_number);
      formData.append("newAgreementNumber", this.newAgreementNumber);
      formData.append("generate_order", generateOrder);
      if (generateOrder && this.pendingOrderDetails) {
        formData.append("order_details", JSON.stringify(this.pendingOrderDetails));
      }
      
      // Send selected customer/department IDs for order creation
      if (this.selectedCustomer && this.selectedCustomer.customer_id) {
        formData.append("selected_customer_id", this.selectedCustomer.customer_id);
      }
      if (this.selectedDepartmentId) {
        const dept = this.departments.find(d => d._id === this.selectedDepartmentId);
        if (dept) {
          formData.append("selected_unit_id", dept.unit_id || '');
          formData.append("selected_unit_name", dept.name || '');
        }
      }

      // New agreement data
      formData.append("e_bill", this.redrawInfo.e_bill || '');
      formData.append("customer_name", this.redrawInfo.customer_name);
      formData.append("company_number", this.redrawInfo.company_number);
      formData.append("customer_phone", this.redrawInfo.customer_phone);
      formData.append("billing_adress", this.redrawInfo.billing_adress);
      formData.append("billing_zip_code", this.redrawInfo.billing_zip_code);
      formData.append("billing_city", this.redrawInfo.billing_city);
      formData.append("customer_contact", this.redrawInfo.customer_contact);
      formData.append("customer_reference", this.redrawInfo.customer_reference);
      formData.append("dealer_name", this.redrawInfo.dealer_name);
      formData.append("dealer_company_number", this.redrawInfo.dealer_company_number);
      formData.append("dealer_phone", this.redrawInfo.dealer_phone);
      formData.append("dealer_address", this.redrawInfo.dealer_address);
      formData.append("dealer_zip_code", this.redrawInfo.dealer_zip_code);
      formData.append("dealer_city", this.redrawInfo.dealer_city);
      formData.append("seller", this.redrawInfo.seller);
      formData.append("contract_start", this.redrawInfo.contract_start);
      // If freetext is selected, send the freetext value instead of the marker
      formData.append("contract_period", this.getContractPeriodValue());
      formData.append("setup_cost", this.redrawInfo.setup_cost);
      formData.append("rental_month_cost", this.redrawInfo.rental_month_cost);
      formData.append("cash_sum", cash_sum);
      formData.append("payment_terms", this.getPaymentTermsValue());
      formData.append("sim_card", this.redrawInfo.sim_card);
      formData.append("alarm_center", this.redrawInfo.alarm_center);
      formData.append("special_terms", this.redrawInfo.special_terms);
      formData.append("agreement_category", this.redrawInfo.agreement_category);
      formData.append("agreement_type", this.redrawInfo.agreement_type);
      formData.append("insurance", this.redrawInfo.insurance === true || this.redrawInfo.insurance === 'yes');
      formData.append("products", JSON.stringify(this.redrawInfo.products));
      formData.append("order_products", JSON.stringify(generateOrder ? this.buildSelectedOrderProducts() : []));
      formData.append("software_service_cost", this.computeSoftwareServiceCost(this.redrawInfo, Number(this.getContractPeriodValue()) || 0));
      formData.append("hardware_cost", this.computeHardwareCost(this.redrawInfo));
      if (this.delivery_period) formData.append("delivery_period", this.delivery_period);
      
      if (this.selected_pdf_file) formData.append("pdf", this.selected_pdf_file);

      this.axios
        .post(`${process.env.VUE_APP_SERVER_URL}/agreements/redraw`, formData, headers)
        .then((response) => {
          this.$emit("agreementRedrawn", response.data);
          this.$modal.hide("redraw-agreement");
          
          // Show success message with order and device transfer info
          const data = response.data.data;
          let message = this.$t('agreements.redraw_success');
          
          if (data.devicesTransferred > 0) {
            message += ` - ${data.devicesTransferred} ${this.$t('agreements.devices_transferred')}`;
          }
          if (data.orderGenerated && data.orderId) {
            message += ` - Order skapad`;
          }
          
          this.handleSuccess(message);
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    fileChangeCreate() {
      if (this.$refs.pdfCreate.files.length > 0) this.selected_pdf_file = this.$refs.pdfCreate.files[0];
      else this.selected_pdf_file = null;
    },

    resetModal() {
      // Reset file selection and product arrays when modal fully closes
      this.selected_pdf_file = null;
      this.defaultProducts = [];

      // Reset customer & department selection
      this.customerSearchQuery = '';
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      this.selectedCustomer = null;
      this.departments = [];
      this.selectedDepartmentId = '';
      this.delivery_period = null;
      
      // Note: newAgreementNumber is not reset here - it will be regenerated when modal reopens
      // Note: expandedCategories/Subcategories/Variants are reset in fetchAndMergeProducts()
      // when modal reopens, to ensure proper initialization based on actual product qty
      // NOTE: productsFromDB is intentionally NOT reset here — it keeps the cache so that
      // re-opening the same agreement (same prop reference → watcher won't fire) still has
      // product data available for cost calculations.
      
      // Reset validation
      if (this.$refs.formRedrawAgreement) {
        this.$nextTick(() => {
          if (this.$refs.formRedrawAgreement) {
            this.$refs.formRedrawAgreement.reset();
          }
        });
      }
    },

    hide(id) {
      this.$modal.hide(id);
    },

    async fetchAndMergeProducts(existingProducts) {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`, { params: { country: this.agreementCountry() } });
        this.productsFromDB = response.data.data;
        
        // First, create a map of flattened variants from old agreement
        // Format: "ProductName - VariantName" -> { qty, prices, etc }
        const flattenedVariantsMap = new Map();
        existingProducts.forEach(ep => {
          if (ep.name && ep.name.includes(' - ') && ep.variant_name) {
            // This is a flattened variant from old agreement
            flattenedVariantsMap.set(ep.name, ep);
          }
        });
        
        // Transform all products from DB to agreement format
        const allProducts = this.productsFromDB.map(dbProduct => {
          // Check if this product exists in the original agreement
          // Match by name, display_name, or _id
          const existingProduct = existingProducts.find(ep => {
            // Match by name
            if (ep.name === dbProduct.name) return true;
            // Match by display_name
            if (dbProduct.display_name && ep.name === dbProduct.display_name) return true;
            // Match by _id (convert to string for safe comparison)
            const epId = ep._id ? String(ep._id) : null;
            const dbId = dbProduct._id ? String(dbProduct._id) : null;
            if (epId && dbId && epId === dbId) return true;
            return false;
          });
          
          if (existingProduct) {
            // Use existing product data (preserves qty, prices etc.)
            // Map 'price' to 'rental_price' if rental_price is not set
            const rentalPrice = existingProduct.rental_price !== undefined ? existingProduct.rental_price : existingProduct.price;
            const hasDbVariants = dbProduct.hasVariants && dbProduct.variants && dbProduct.variants.length > 0;
            // Remove flattened-format fields that should not exist on parent products
            // (these can leak from existingProduct when it was stored from a flattened variant match)
            // eslint-disable-next-line no-unused-vars
            const { variant_name: _vn, variant_id: _vi, ...cleanExistingProduct } = existingProduct;
            return {
              ...cleanExistingProduct,
              _id: dbProduct._id,
              name: dbProduct.name,
              articleNumber: dbProduct.articleNumber || existingProduct.articleNumber || '',
              display_name: dbProduct.display_name || existingProduct.display_name || null,
              is_software: dbProduct.is_software || existingProduct.is_software || false,
              always_visible: dbProduct.always_visible || existingProduct.always_visible || false,
              category: dbProduct.category || existingProduct.category,
              subcategory: dbProduct.subcategory || existingProduct.subcategory || null,
              hasVariants: dbProduct.hasVariants || false,
              // For products with variants, parent qty should always be 0
              // (qty belongs to individual variants, not the parent)
              qty: hasDbVariants ? 0 : (existingProduct.qty || 0),
              rental_price: hasDbVariants ? null : (rentalPrice || null),
              price: hasDbVariants ? null : (rentalPrice || null),
              cash_price: existingProduct.cash_price || null,
              purchase_price: dbProduct.purchase_price || existingProduct.purchase_price || 0,
              variants: (dbProduct.variants || []).map(dbVariant => {
                // Check if this variant exists in the existing product (nested structure)
                // Multiple matching strategies for robust matching
                const existingVariant = (existingProduct.variants || []).find(v => {
                  // Strategy 1: Match by name (most reliable)
                  if (v.name === dbVariant.name) return true;
                  
                  // Strategy 2: Match by ID (convert to strings for safe comparison)
                  const vId = v._id ? String(v._id) : null;
                  const dbId = dbVariant._id ? String(dbVariant._id) : null;
                  if (vId && dbId && vId === dbId) return true;
                  
                  // Strategy 3: Match by variant_name field (some formats use this)
                  if (v.variant_name && v.variant_name === dbVariant.name) return true;
                  
                  return false;
                });
                
                // Also check for flattened variant format: "ProductName - VariantName"
                const flattenedKey = `${dbProduct.name} - ${dbVariant.name}`;
                const flattenedVariant = flattenedVariantsMap.get(flattenedKey);
                
                // Check with display_name too
                const flattenedKeyWithDisplay = dbProduct.display_name ? `${dbProduct.display_name} - ${dbVariant.name}` : null;
                const flattenedVariantAlt = flattenedKeyWithDisplay ? flattenedVariantsMap.get(flattenedKeyWithDisplay) : null;
                
                // Use data from flattened variant if found, otherwise from nested variant
                const sourceVariant = flattenedVariant || flattenedVariantAlt || existingVariant;
                
                if (sourceVariant) {
                  const variantRentalPrice = sourceVariant.rental_price !== undefined ? sourceVariant.rental_price : sourceVariant.price;
                  const variantCashPrice = sourceVariant.cash_price || null;
                  
                  return {
                    ...dbVariant,
                    articleNumber: dbVariant.articleNumber || sourceVariant.articleNumber || '',
                    qty: sourceVariant.qty || 0,
                    rental_price: variantRentalPrice,
                    price: variantRentalPrice, // Keep both for compatibility
                    cash_price: variantCashPrice,
                    purchase_price: dbVariant.purchase_price ?? sourceVariant.purchase_price ?? 0,
                    recommended_rental_price: dbVariant.recommended_rental_price || 0,
                    recommended_cash_price: dbVariant.recommended_cash_price || 0
                  };
                }
                
                // No data found for this variant
                return {
                  ...dbVariant,
                  articleNumber: dbVariant.articleNumber || '',
                  qty: 0,
                  rental_price: null,
                  price: null,
                  cash_price: null,
                  purchase_price: dbVariant.purchase_price || 0,
                  recommended_rental_price: dbVariant.recommended_rental_price || 0,
                  recommended_cash_price: dbVariant.recommended_cash_price || 0
                };
              }),
              recommended_rental_price: dbProduct.recommended_rental_price || 0,
              recommended_cash_price: dbProduct.recommended_cash_price || 0,
            };
          } else {
            // Product not found as exact match, but check if any of its variants exist as flattened products
            // OR nested in existing products
            const variantsWithData = (dbProduct.variants || []).map(dbVariant => {
              let sourceVariant = null;
              
              // Strategy 1: Check flattened format with product name
              const flattenedKey = `${dbProduct.name} - ${dbVariant.name}`;
              sourceVariant = flattenedVariantsMap.get(flattenedKey);
              
              // Strategy 2: Check flattened format with display_name
              if (!sourceVariant && dbProduct.display_name) {
                const flattenedKeyWithDisplay = `${dbProduct.display_name} - ${dbVariant.name}`;
                sourceVariant = flattenedVariantsMap.get(flattenedKeyWithDisplay);
              }
              
              if (sourceVariant) {
                const variantRentalPrice = sourceVariant.rental_price !== undefined ? sourceVariant.rental_price : sourceVariant.price;
                return {
                  ...dbVariant,
                  articleNumber: dbVariant.articleNumber || sourceVariant.articleNumber || '',
                  qty: sourceVariant.qty || 0,
                  rental_price: variantRentalPrice || null,
                  price: variantRentalPrice || null,
                  cash_price: sourceVariant.cash_price || null,
                  purchase_price: dbVariant.purchase_price ?? sourceVariant.purchase_price ?? 0,
                  recommended_rental_price: dbVariant.recommended_rental_price || 0,
                  recommended_cash_price: dbVariant.recommended_cash_price || 0
                };
              }
              
              // No data for this variant
              return {
                ...dbVariant,
                articleNumber: dbVariant.articleNumber || '',
                qty: 0,
                rental_price: null,
                price: null,
                cash_price: null,
                purchase_price: dbVariant.purchase_price || 0,
                recommended_rental_price: dbVariant.recommended_rental_price || 0,
                recommended_cash_price: dbVariant.recommended_cash_price || 0
              };
            });
            
            // New product from DB, initialize with null prices (placeholder will show recommended)
            return {
              _id: dbProduct._id,
              articleNumber: dbProduct.articleNumber || '',
              display_name: dbProduct.display_name || null,
              name: dbProduct.name,
              type: dbProduct.type,
              category: dbProduct.category,
              subcategory: dbProduct.subcategory || null,
              hasVariants: dbProduct.hasVariants || false,
              variants: variantsWithData, // Use the variants with data from flattened products
              qty: 0,
              rental_price: null,
              price: null,
              cash_price: null,
              purchase_price: dbProduct.purchase_price || 0,
              is_software: dbProduct.is_software || false,
              always_visible: dbProduct.always_visible || false,
              recommended_rental_price: dbProduct.recommended_rental_price || 0,
              recommended_cash_price: dbProduct.recommended_cash_price || 0,
            };
          }
        });
        
        this.redrawInfo.products = allProducts;
        this.calculateTotalPrice();
        
        // Auto-expand ONLY categories and subcategories that have products with qty > 0 from old agreement
        this.$nextTick(() => {
          // First, reset all to collapsed
          this.expandedCategories = {};
          this.expandedSubcategories = {};
          this.expandedVariants = {};
          
          // Then expand only those with qty > 0
          allProducts.forEach(p => {
            const hasMainProductQty = p.qty > 0;
            const hasVariantQty = p.variants && p.variants.some(v => v.qty > 0);
            
            if (hasMainProductQty || hasVariantQty) {
              // Expand category
              if (p.category) {
                this.$set(this.expandedCategories, p.category, true);
              }
              // Expand subcategory
              if (p.subcategory) {
                const subcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
                this.$set(this.expandedSubcategories, subcatId, true);
              }
              // Expand variants dropdown if any variant has qty > 0
              if (hasVariantQty && p._id) {
                this.$set(this.expandedVariants, p._id, true);
              }
            }
          });
        });
      } catch (error) {
        console.error('Failed to fetch products:', error);
        // Fallback to existing products if fetch fails
        this.redrawInfo.products = existingProducts.length > 0 ? existingProducts : [];
      }
    },

    fetchProductsFromOrder(agreementNumber) {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/admin-order?agreement_number=${agreementNumber}`)
        .then((response) => {
          if (response.data.data && response.data.data.length > 0) {
            const orderProducts = response.data.data[0].products;
            this.redrawInfo.products = this.mergeProducts(orderProducts);
          } else {
            this.redrawInfo.products = _.cloneDeep(this.defaultProducts);
          }
        })
        .catch((error) => {
          console.error('Error fetching order products:', error);
          this.redrawInfo.products = _.cloneDeep(this.defaultProducts);
        });
    },

    mergeProducts(existingProducts) {
      return existingProducts;
    },

    shouldShowRentalForProduct(product) {
      const category = this.redrawInfo.agreement_category;
      
      if (category === 'rental' || category === 'financed') {
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) {
          return false;
        }
        return true;
      }
      
      if (category === 'cash_services') {
        if (product.is_software === true) return true;
        if (product.category === 'simcard' || product.category === 'software') return true;
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('monthly')) return true;
        return false;
      }
      
      return false;
    },
    
    shouldShowCashForProduct(product) {
      const category = this.redrawInfo.agreement_category;
      
      if (category === 'rental' || category === 'financed') {
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) {
          return true;
        }
        return false;
      }
      
      if (category === 'cash_services') {
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) return true;
        if (!product.is_software && product.category !== 'simcard' && product.category !== 'software' &&
            !((product.category === 'accessory' || product.category === 'other') && 
              product.type && product.type.includes('monthly'))) return true;
        return false;
      }
      
      return false;
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    if (this.user && /(^admin$)|(^seller$)/.test(this.user.role)) {
      this.loadCountryLists();
    }
  },
};
</script>
